import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, 
  X, 
  CheckCircle, 
  Ticket, 
  MessageSquare, 
  UserPlus,
  Clock,
  ExternalLink
} from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';

const NotificationDropdown = ({ isOpen, onClose, position }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const dropdownRef = useRef(null);
  const { notifications, unreadCount, markAsRead, markAllAsRead, notificationsMuted } =
    useNotifications();
  
  const recentNotifications = notifications.slice(0, 5);

  const getNotificationRoute = () => {
    if (user?.role === 'admin' || user?.role === 'employee') {
      return '/admin/notifications';
    }
    return '/customer/notifications';
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen, onClose]);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'ticket_update':
      case 'ticket_resolved': return <Ticket className="w-4 h-4" />;
      case 'message': return <MessageSquare className="w-4 h-4" />;
      case 'assignment': return <UserPlus className="w-4 h-4" />;
      default: return <Bell className="w-4 h-4" />;
    }
  };

  // Fixed Colors for Dark Mode
  const getNotificationColor = (type) => {
    switch (type) {
      case 'ticket_resolved':
        return 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400';
      case 'ticket_update':
        return 'bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400';
      case 'message':
        return 'bg-purple-100 text-purple-600 dark:bg-purple-500/20 dark:text-purple-400';
      default:
        return 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-400';
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
      onClose();
    }
  };

  const formatTime = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      return date.toLocaleDateString();
    } catch {
      return 'Recently';
    }
  };

  if (!isOpen) return null;

  const dropdownWidth = 384; 
  const dropdownHeight = Math.min(600, window.innerHeight - position.top - 20);
  const topPosition = position.top;
  let leftPosition = position.left || 0;
  const maxLeft = window.innerWidth - dropdownWidth - 16;
  if (leftPosition + dropdownWidth > window.innerWidth - 16) {
    leftPosition = maxLeft;
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black/20 dark:bg-black/50 z-[9998]"
        onClick={onClose}
      />
      
      <div
        ref={dropdownRef}
        className="fixed bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 z-[9999] flex flex-col overflow-hidden"
        style={{
          top: `${topPosition}px`,
          left: `${leftPosition}px`,
          width: '384px',
          maxHeight: `${dropdownHeight}px`,
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-800 bg-gradient-to-r from-purple-600 to-indigo-600">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-white" />
            <h3 className="text-lg font-bold text-white">Notifications</h3>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 bg-white/20 backdrop-blur-sm text-white text-xs font-bold rounded-full">
                {unreadCount} new
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Actions */}
        {unreadCount > 0 && (
          <div className="p-3 border-b border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-800/50">
            <button
              onClick={markAllAsRead}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
            >
              <CheckCircle className="w-3.5 h-3.5" />
              Mark all as read
            </button>
          </div>
        )}

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {recentNotifications.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bell className="w-8 h-8 text-gray-400 dark:text-slate-600" />
              </div>
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                {notificationsMuted ? 'Notifications muted (testing)' : 'No notifications'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {notificationsMuted
                  ? 'Turn them back on from the sidebar when you are done testing.'
                  : "You're all caught up!"}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-slate-800">
              {recentNotifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-4 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors cursor-pointer ${
                    !notification.is_read ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
                  }`}
                >
                  <div className="flex gap-3">
                    <div className={`p-2 rounded-lg flex-shrink-0 h-fit ${getNotificationColor(notification.type)}`}>
                      {getNotificationIcon(notification.type)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className={`text-sm font-semibold leading-tight ${
                          !notification.is_read ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'
                        }`}>
                          {notification.title}
                        </h4>
                        {!notification.is_read && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5 shadow-sm shadow-blue-500/50"></div>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 dark:text-slate-400 mb-2 line-clamp-2 leading-relaxed">
                        {notification.message}
                      </p>
                      <div className="flex items-center gap-2 text-[10px] text-gray-400 dark:text-slate-500 font-medium uppercase tracking-wide">
                        <Clock className="w-3 h-3" />
                        <span>{formatTime(notification.created_at)}</span>
                        {notification.link && (
                          <>
                            <span>•</span>
                            <span className="flex items-center gap-1 text-indigo-600 dark:text-indigo-400">
                              View <ExternalLink className="w-3 h-3" />
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {recentNotifications.length > 0 && (
          <div className="p-3 border-t border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-900">
            <button
              onClick={() => {
                navigate(getNotificationRoute());
                onClose();
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            >
              View All Notifications
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationDropdown;
